import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Users, Download, Loader2, Shield, Flag } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [users, setUsers] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const togglePlan = async (u) => {
    const next = u.plan === "paid" ? "trial" : "paid";
    try {
      await api.post(`/admin/users/${u.id}/plan`, { plan: next });
      toast.success(`${u.email} → ${next}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "mytrack_users.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const totalRuns = (users || []).reduce((s, u) => s + (u.runCount || 0), 0);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-mt-primary">
            <Shield size={16} /> <span className="text-xs font-bold uppercase tracking-[0.25em]">Admin</span>
          </div>
          <h1 className="mt-1 font-head text-3xl font-black tracking-tighter">Dashboard</h1>
        </div>
        <Button onClick={exportCsv} data-testid="admin-export-csv" className="gap-2 bg-mt-primary text-white hover:bg-mt-primary/90">
          <Download size={16} /> Export Emails (CSV)
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Stat label="Total Users" value={users ? users.length : "—"} icon={Users} />
        <Stat label="Total Runs" value={users ? totalRuns : "—"} icon={Flag} />
        <Stat label="Admins" value={users ? users.filter((u) => u.role === "admin").length : "—"} icon={Shield} />
      </div>

      <div className="overflow-hidden rounded-xl border border-mt-border bg-mt-card shadow-sm" data-testid="admin-users-table">
        <div className="grid grid-cols-12 gap-2 border-b border-mt-border bg-mt-secondary/50 px-4 py-3 text-xs font-bold uppercase tracking-wider text-mt-mutedfg">
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Name</div>
          <div className="col-span-2">Joined</div>
          <div className="col-span-1 text-right">Runs</div>
          <div className="col-span-3 text-right">Plan</div>
        </div>
        {users === null ? (
          <div className="grid place-items-center py-12"><Loader2 className="animate-spin text-mt-primary" size={24} /></div>
        ) : users.length === 0 ? (
          <div className="py-12 text-center text-mt-mutedfg">No users yet.</div>
        ) : (
          users.map((u) => (
            <div key={u.id} data-testid={`admin-user-${u.email}`} className="grid grid-cols-12 items-center gap-2 border-b border-mt-border px-4 py-3 text-sm last:border-0">
              <div className="col-span-4 truncate">
                <span className="font-semibold">{u.email}</span>
                {u.role === "admin" && <span className="ml-2 rounded bg-mt-primary px-1.5 py-0.5 text-[10px] font-bold text-white">ADMIN</span>}
              </div>
              <div className="col-span-2 truncate text-mt-mutedfg">{u.name || "—"}</div>
              <div className="col-span-2 font-mono text-xs text-mt-mutedfg">{(u.created_at || "").slice(0, 10)}</div>
              <div className="col-span-1 text-right font-mono font-bold">{u.runCount}</div>
              <div className="col-span-3 flex items-center justify-end gap-2">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${u.plan === "paid" ? "bg-mt-success text-white" : "bg-mt-muted text-mt-text"}`}>{(u.plan || "trial").toUpperCase()}</span>
                <button onClick={() => togglePlan(u)} data-testid={`admin-toggle-plan-${u.email}`} className="rounded-md border border-mt-border px-2 py-1 text-xs font-bold transition-colors hover:border-mt-primary hover:text-mt-primary">
                  {u.plan === "paid" ? "Set Trial" : "Mark Paid"}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-mt-border bg-mt-card p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-mt-mutedfg">{label}</span>
        <span className="grid h-8 w-8 place-items-center rounded-md bg-mt-secondary"><Icon size={16} /></span>
      </div>
      <div className="mt-3 font-mono text-3xl font-bold">{value}</div>
    </div>
  );
}
